// The tab strip of the phone board: the column names, one tab per column.
// Written out rather than built on MUI Tabs: the strip has to follow a
// scroll-snap scroller that MUI knows nothing about, and the indicator is just
// the active tab's own border.

import { useEffect, useRef } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { alpha } from '@mui/material/styles';

export interface ColumnTab {
    id: string;
    title: string;
}

export interface ColumnTabsProps {
    tabs: ColumnTab[];
    active: number;
    onSelect: (index: number) => void;
}

export const tabDomId = (id: string) => `board-tab-${id}`;
export const panelDomId = (id: string) => `board-panel-${id}`;

export const TAB_HEIGHT = 46;
// Up to this many tabs share the width equally; more scroll sideways.
const EQUAL_WIDTH_MAX = 4;
const SCROLLING_TAB_MIN_WIDTH = 96;

export function ColumnTabs({ tabs, active, onSelect }: ColumnTabsProps) {
    const refs = useRef<(HTMLButtonElement | null)[]>([]);
    const equal = tabs.length <= EQUAL_WIDTH_MAX;

    // A strip that scrolls keeps the active tab on screen.
    useEffect(() => {
        if (equal) return;
        refs.current[active]?.scrollIntoView?.({
            block: 'nearest',
            inline: 'nearest',
        });
    }, [active, equal]);

    if (tabs.length === 0) return null;

    const move = (to: number) => {
        const next = (to + tabs.length) % tabs.length;
        onSelect(next);
        refs.current[next]?.focus();
    };

    const onKeyDown = (event: React.KeyboardEvent, index: number) => {
        switch (event.key) {
            case 'ArrowRight':
                move(index + 1);
                break;
            case 'ArrowLeft':
                move(index - 1);
                break;
            case 'Home':
                move(0);
                break;
            case 'End':
                move(tabs.length - 1);
                break;
            default:
                return;
        }
        event.preventDefault();
    };

    return (
        <Box
            role="tablist"
            aria-label="Board columns"
            sx={{
                display: 'grid',
                gridAutoFlow: 'column',
                gridAutoColumns: equal
                    ? 'minmax(0, 1fr)'
                    : `minmax(${SCROLLING_TAB_MIN_WIDTH}px, max-content)`,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                flexShrink: 0,
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            {tabs.map((tab, index) => {
                const selected = index === active;
                return (
                    <ButtonBase
                        key={tab.id}
                        ref={el => {
                            refs.current[index] = el;
                        }}
                        role="tab"
                        id={tabDomId(tab.id)}
                        aria-selected={selected}
                        aria-controls={panelDomId(tab.id)}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onSelect(index)}
                        onKeyDown={event => onKeyDown(event, index)}
                        sx={theme => ({
                            height: TAB_HEIGHT,
                            px: 1,
                            fontFamily: 'inherit',
                            fontSize: 15,
                            fontWeight: selected ? 700 : 400,
                            color: selected
                                ? theme.palette.primary.main
                                : alpha(theme.palette.text.primary, 0.65),
                            borderBottom: '2px solid',
                            borderColor: selected
                                ? theme.palette.primary.main
                                : 'transparent',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            minWidth: 0,
                        })}
                    >
                        {tab.title}
                    </ButtonBase>
                );
            })}
        </Box>
    );
}
